/**
 * CA 在线申请 · 接口集中封装（已全部正式联调，直连真实接口）。
 * 接口依据：src/views/ca/assets/接口文档.md（后端 ebs-zcjfbiz-ca 模块）。
 * 发票：申请开票 applyInvoice（文档 §4.8）、查询发票信息 getInvoice（文档 §4.9，
 * 响应含 applyOrgName / licenceCode 供未申请时默认带入抬头与识别号）；
 * 发票 PDF 复用通用文件下载 /zcjf/base/file/download/{invoiceFileId}，不单独封装。
 */
import request from '@/util/http'

/** 1. 申请页导航栏信息（顶栏品牌：平台名 sysName / 主题色 / logo / 官网链接），免登录 */
export function getApplyNav(params) {
  return request({ url: '/zcjf/ca/apply/nav', method: 'get', params })
}

/**
 * 2. 申请页整体配置（文档 §3.3 CaApplyInfoVo：customer / system / productList / templateFileList），免登录。
 * 接口结果原样返回，页面直接使用 VO 字段。
 */
export function getPageData(params) {
  return request({ url: '/zcjf/ca/apply/applyInfo', method: 'get', params })
}

/**
 * 3. 提交 CA 订单（文档 §4.4）。
 * 入参 CaOrderSubmitVo 由申请页 buildPayload 组装（表单 key 与后端一致，无需改名）；
 * 返回 { orderId, orderCode, productAmount, totalAmount, payType }。
 */
export function submitOrder(data) {
  return request({ url: '/zcjf/ca/order/submit', method: 'post', data })
}

/**
 * 4. 订单支付签名（文档 §5.1，网上支付前置；仅「待付款」的网上支付订单可用）。
 * 返回 CaPayInfoVo，前端用 util/pay.js 的 payCaOrder 构造隐藏域表单 POST 到支付平台。
 */
export function paySign(params) {
  return request({ url: '/zcjf/ca/order/signOrder', method: 'get', params })
}

/**
 * 5. 我的订单分页（文档 §4.1）。
 * status 为文档 §9.1 数字码库值（选项见 util/caForm.js ORDER_STATUS_OPTIONS），原样透传精准匹配；
 * 返回 records 不做转换（orderStatus 为库值原值，列表页按钮/状态文字直接按库值判断）。
 */
export function getOrderPage(params) {
  return request({ url: '/zcjf/ca/order/my/page', method: 'get', params })
}

/** 6. 我的订单详情（文档 §4.2；出参原样返回，页面直接使用 VO 字段） */
export function getOrderDetail(params) {
  return request({ url: '/zcjf/ca/order/my/detail', method: 'get', params })
}

/** 7. 用户取消订单（文档 §4.3，仅「待付款」可取消；cancelReason 为空后端记「用户主动取消」） */
export function cancelOrder(data) {
  return request({ url: '/zcjf/ca/order/cancel', method: 'post', data })
}

/**
 * 8. 查询电汇收款账户（文档 §4.5，无入参；跨服务取担保默认打款账户）。
 * 账户信息可能变更，每次进入电汇页都要实时调用，不要缓存。
 */
export function getPayAccount() {
  return request({ url: '/zcjf/ca/order/payAccount', method: 'get' })
}

/**
 * 9. 查询电汇信息（文档 §4.6，仅电汇订单）。
 * 返回 { orderCode, totalAmount（应汇金额）, orderStatus, orderStatusName, wireVoucherFileId }。
 */
export function getWireInfo(params) {
  return request({ url: '/zcjf/ca/order/wireInfo', method: 'get', params })
}

/** 10. 上传电汇付款凭证（文档 §4.7；待确认期间重复上传为覆盖，上传后进入「待电汇确认」） */
export function submitWireVoucher(data) {
  return request({ url: '/zcjf/ca/order/wireVoucher', method: 'post', data })
}

/**
 * 11. 驳回后补充材料重新提交（文档 §4.10）。
 * 仅「已驳回」订单；非空字段覆盖、商品/金额不可改，提交后回到「审核中」。
 * 入参 { orderId, ...form }（表单 key 与后端一致，编辑页按厂商拣选后展开）。
 */
export function resubmitOrder(data) {
  return request({ url: '/zcjf/ca/order/resubmit', method: 'post', data })
}

/* ---- 12–13. 上海CA 信息认证（申请 / 续期下单，返回 { authUrl } 由前端新窗口打开） ---- */

/** 12. 上海CA-证书申请下单（非续签订单），入参 { orderId, frontRedirectUrl（认证完成后跳回的前端全路径地址） }，返回 { authUrl } 认证 H5 链接 */
export function shcaAuthApply(params) {
  return request({ url: '/zcjf/ca/sheca/apply', method: 'post', params })
}

/**
 * 13. 上海CA-续期下单（续期订单），返回 { authUrl } 认证 H5 链接。
 * 入参 { orderId, frontRedirectUrl（认证完成后跳回的前端全路径地址）,
 *        caSerialNo（必传，CA 证书序列号，由 CA 登录弹框读取 UKey 证书得到，见 util/shecaCa.js） }
 */
export function shcaAuthRenew(params) {
  return request({ url: '/zcjf/ca/sheca/renew', method: 'post', params })
}

/**
 * 14. 上海CA-申请订单确认收货（文档 §4.11）。
 * 仅上海CA 首次申请订单本人可操作，订单「50 已寄出」后可确认；携带订户客户端环境，
 * 成功后订单流转「60 已完成」并记录签收时间，订户将收到 PIN 码短信。
 * 入参 { orderId }；返回 { ok: true }。
 */
export function shcaConfirmReceive(params) {
  return request({ url: '/zcjf/ca/sheca/confirmReceive', method: 'post', params })
}

/**
 * 15. 申请开票（文档 §4.8）。
 * 仅「已寄出 / 已完成」且未开票的订单可申请；一单一票，申请后开票状态 0 → 2。
 * 开票失败（invoiceStatus=3）重提复用本接口。
 * 入参：{ orderId, invoiceType, invoiceTitle, taxNumber, invoiceAddress, invoicePhone,
 *        invoiceBank, invoiceBankNo, invoiceRemark, invoiceEmail }
 * 其中 invoiceBankNo / invoiceRemark 为扩展字段（文档未含，前端先传、后端补）。
 */
export function applyInvoice(data) {
  return request({ url: '/zcjf/ca/order/invoice/apply', method: 'post', data })
}

/**
 * 16. 查询发票信息（文档 §4.9，申请/查看弹框打开时调用）。
 * 返回 CaInvoiceVo：invoiceStatus / invoiceType / invoiceTitle / taxNumber /
 * invoiceAddress / invoicePhone / invoiceBank / invoiceBankNo / invoiceRemark / invoiceEmail；
 * 另含 applyOrgName / licenceCode——未申请（invoiceStatus=0）时明细为空，
 * 前端用这两个字段默认带入发票抬头与纳税人识别号。
 */
export function getInvoice(params) {
  return request({ url: '/zcjf/ca/order/invoice', method: 'get', params })
}

/* ---- 17–20. CFCA 在线续签（文档 §11；新规 RA 交易：5122/5121 身份核验 → 5106 换发凭证 → 5102 制证） ----
 * 状态门槛（文档 §11.0）：authStart 限「30 待认证 / 80 已驳回」；submit 限「40 认证通过」；
 * ra5102 不查订单（凭证合法性由 CFCA 保证）；updateOrder 不限状态（插件导入成功后即调）。
 * 20→30（初审）由管理端操作；30/80→40 由 CFCA 核验回调直接驱动（无管理端登记节点），
 * 前端轮询订单详情观察状态变化。
 */

/**
 * 17. 身份核验开始（文档 §11.1）：按订单订户信息调 CFCA RA 5122 发起身份核验，限 30/80。
 * 后端先 5121 预检：未失效流水号（0 未激活 / 1 已激活 / 5 人工审核中）直接复用，
 * 不重复发短信、不产生新流水；失效 / 已使用 / 人工审核不通过才重新发起 5122。
 * 入参 { orderId }；返回 { authId, authUrl, status（0/1/5）, authExpiryTime（yyyyMMddHHmmss）,
 * transId, remark（人工审核等特殊提示） }。核验对象由订单产品自动决定，组合订单只发起一次。
 * 核验结果由 CFCA 回调（文档 §11.6，前端不调）直接驱动订单状态：通过 → 40 认证通过（含 80→40）、
 * 驳回 → 80（30→80），人工审核中状态不动。
 */
export function authStartCaRenew(data) {
  return request({ url: '/zcjf/ca/renew/authStart', method: 'post', data })
}

/**
 * 18. 续签提交（文档 §11.2）：限「40 认证通过」。后端以订单 5122 的 authId + 证前核身三要素
 * 调 CFCA RA 5106 换制证凭证。入参 { orderId, dn, certType（1 个人/法人证书，2 企业证书）,
 * limit（续签年限，必填；订单详情 orderLimit 字符串原样上送）, startDate, endDate（yyyyMMdd） }；
 * 返回 { serialNo, authCode, dn（归一化后，生成 P10 用） }。
 * 凭证复用：5106 失败但订单已暂存凭证时后端不报错、直接返回旧凭证（文档 §11.2）；
 * 订单详情（§4.2）也会带出暂存的 serialNo/authCode，前端重试时可跳过本接口直接复用。
 */
export function submitCaOnline(data) {
  return request({ url: '/zcjf/ca/renew/submit', method: 'post', data })
}

/**
 * 19. 续签制证（文档 §11.3）：5106 凭证 + 控件生成的 P10 调 CFCA RA 5102 换双证书（不查订单状态）。
 * 入参 { serialNo, authCode, p10, p10Sub?（国密 SM2 双证书场景，当前 RSA 单证书不传） }；
 * 返回 { signatureCert, encryptionCert, encryptionPrivateKey }（由控件灌回 UKEY）。
 */
export function ra5102(data) {
  return request({ url: '/zcjf/ca/renew/ra5102', method: 'post', data })
}

/**
 * 20. 续签完成（文档 §11.4）：证书灌回 UKEY 成功后回写订单（对应剩余数量 -1、清空暂存凭证，
 * 扣尽时订单流转 60 已完成）。必须在控件导入成功后调用，导入失败不要调。
 * 入参 { orderId, certType }；返回 { ok: true }。
 */
export function updateRenewOrder(data) {
  return request({ url: '/zcjf/ca/renew/updateOrder', method: 'post', data })
}
